import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { buildStayReservationPackRows, resolveReservationPackIds } from "../reservation/reservationPacks";

export const stayReservationPackKey = (reservationId: string) => ["stay-reservation-pack", reservationId];

/** Matilhas operacionais da reserva, com fallback para a coluna legada. */
export function useStayReservationPacks(reservationId: string) {
  return useQuery({
    queryKey: stayReservationPackKey(reservationId),
    enabled: !!reservationId,
    queryFn: async (): Promise<string[]> => {
      const { data: relationData, error: relationError } = await (supabase as any) // eslint-disable-line @typescript-eslint/no-explicit-any
        .from("stay_reservation_packs")
        .select("stay_pack_id, created_at")
        .eq("stay_reservation_id", reservationId)
        .order("created_at", { ascending: true });
      if (relationError) throw relationError;

      const relationRows = (relationData ?? []) as Array<{ stay_pack_id: string }>;
      if (relationRows.length > 0) return resolveReservationPackIds(relationRows, null);

      const { data, error } = await supabase
        .from("stay_reservations")
        .select("stay_pack_id")
        .eq("id", reservationId)
        .single();
      if (error) throw error;
      return resolveReservationPackIds([], data.stay_pack_id);
    },
  });
}

export function useUpdateStayReservationPacks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reservationId, stayPackIds }: { reservationId: string; stayPackIds: string[] }) => {
      const nextPackIds = [...new Set(stayPackIds)];
      const { error } = await supabase
        .from("stay_reservations")
        .update({ stay_pack_id: nextPackIds[0] ?? null })
        .eq("id", reservationId);
      if (error) throw error;

      const { error: deleteError } = await (supabase as any) // eslint-disable-line @typescript-eslint/no-explicit-any
        .from("stay_reservation_packs")
        .delete()
        .eq("stay_reservation_id", reservationId);
      if (deleteError) throw deleteError;

      const rows = buildStayReservationPackRows(reservationId, nextPackIds);
      if (rows.length > 0) {
        const { error: insertError } = await (supabase as any) // eslint-disable-line @typescript-eslint/no-explicit-any
          .from("stay_reservation_packs")
          .insert(rows);
        if (insertError) throw insertError;
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: stayReservationPackKey(variables.reservationId) });
      queryClient.invalidateQueries({ queryKey: ["panel-day"] });
      queryClient.invalidateQueries({ queryKey: ["stay-schedule"] });
    },
  });
}
